import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AuthApiService } from '@/lib/services';
import { clearAllTokens, getAccessToken } from '@/lib/token-manager';
import { queryKeys } from '@/lib/query-config';
import type {
  User,
  LoginRequest,
  LoginResult,
  RegisterRequest,
  ChangePasswordRequest,
  PasswordResetRequest as ForgotPasswordRequest,
  PasswordResetConfirmRequest as ResetPasswordRequest,
  AuthToken,
  SignupOtpSentResponse,
  RequestTwoFactorOtpResponse,
  CompleteTwoFactorLoginRequest,
} from '@welynk/types';
import type {
  TwoFactorSetupInitiateResponse,
  TwoFactorStatusResponse,
  TwoFactorVerifySetupResponse,
} from '@/lib/services/auth-api';

/**
 * Login user.
 * Returns AuthToken on full success, or TwoFactorChallengeResponse when the
 * server needs the TOTP code.  The component decides how to handle each case.
 */
export const useLogin = () => {
  return useMutation({
    mutationFn: async (credentials: LoginRequest): Promise<LoginResult> => {
      return AuthApiService.login(credentials);
    },
  });
};

/**
 * Register user. Response includes the signup OTP reference (otpId) used to
 * verify/resend the emailed 6-digit code.
 */
export const useRegister = () => {
  return useMutation({
    mutationFn: async (
      data: RegisterRequest
    ): Promise<User & Partial<SignupOtpSentResponse> & { alreadyRegistered?: boolean }> => {
      return AuthApiService.register(data);
    },
  });
};

/**
 * Get current user
 */
export const useCurrentUser = () => {
  return useQuery({
    queryKey: queryKeys.auth.user(),
    queryFn: async (): Promise<User | null> => {
      const token = getAccessToken();
      if (!token) return null;

      try {
        return await AuthApiService.getCurrentUser();
      } catch {
        return null;
      }
    },
    enabled: !!getAccessToken(),
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false, // Don't retry if user fetch fails
  });
};

/**
 * Change password
 */
export const useChangePassword = () => {
  return useMutation({
    mutationFn: async (data: ChangePasswordRequest): Promise<{ message: string }> => {
      await AuthApiService.changePassword(data);
      return { message: 'Password changed successfully' };
    },
  });
};

/**
 * Logout — revokes the refresh token on the server, then clears local state.
 * Server revocation is best-effort: local tokens are always cleared even if the
 * API call fails (e.g., network error or already-expired token).
 */
export const useLogout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      try {
        // The refresh token travels via the httpOnly cookie; the server
        // revokes the family and clears the cookie.
        await AuthApiService.logout();
      } catch {
        // Best-effort: always clear local state regardless of server response
      }
      clearAllTokens();
    },
    onSuccess: () => {
      queryClient.clear();
      // Import lazily to avoid circular deps at module init time
      import('./store').then(({ useAuthStore }) => {
        useAuthStore.getState().clearUser();
      });
    },
  });
};

/**
 * Verify the 6-digit signup OTP and activate the account
 */
export const useVerifySignupOtp = () => {
  return useMutation({
    mutationFn: async (data: { otpId: string; code: string }): Promise<{ message: string }> => {
      return AuthApiService.verifySignupOtp(data.otpId, data.code);
    },
  });
};

/**
 * Resend the signup OTP. Pass { otpId } when already known, or { email }
 * when it isn't (e.g. login redirected here for an unverified account).
 */
export const useResendSignupOtp = () => {
  return useMutation({
    mutationFn: async (
      params: { otpId: string } | { email: string }
    ): Promise<Partial<SignupOtpSentResponse> & { message: string }> => {
      return AuthApiService.resendSignupOtp(params);
    },
  });
};

/**
 * Request an emailed OTP for a 2FA-pending login
 */
export const useRequestTwoFactorOtp = () => {
  return useMutation({
    mutationFn: async (
      tempToken: string
    ): Promise<RequestTwoFactorOtpResponse & { message: string }> => {
      return AuthApiService.requestTwoFactorOtp(tempToken);
    },
  });
};

/**
 * Complete a 2FA-pending login with a TOTP code, an email OTP, or a backup code
 */
export const useCompleteTwoFactorLogin = () => {
  return useMutation({
    mutationFn: async (data: CompleteTwoFactorLoginRequest): Promise<AuthToken> => {
      return AuthApiService.completeTwoFactorLogin(data);
    },
  });
};

/**
 * Forgot password (request reset email)
 */
export const useForgotPassword = () => {
  return useMutation({
    mutationFn: async (data: ForgotPasswordRequest): Promise<{ message: string }> => {
      return AuthApiService.requestPasswordReset(data);
    },
  });
};

/**
 * Reset password using token
 */
export const useResetPassword = () => {
  return useMutation({
    mutationFn: async (data: ResetPasswordRequest): Promise<{ message: string }> => {
      return AuthApiService.resetPassword(data);
    },
  });
};

/**
 * Get 2FA status for current user
 */
export const useTwoFactorStatus = () => {
  return useQuery({
    queryKey: queryKeys.auth.twoFactorStatus(),
    queryFn: async (): Promise<TwoFactorStatusResponse> => {
      return AuthApiService.getTwoFactorStatus();
    },
    enabled: !!getAccessToken(),
    retry: false,
  });
};

/**
 * Initiate 2FA setup
 */
export const useInitiateTwoFactorSetup = () => {
  return useMutation({
    mutationFn: async (): Promise<TwoFactorSetupInitiateResponse> => {
      return AuthApiService.initiateTwoFactorSetup();
    },
  });
};

/**
 * Verify 2FA setup with code
 */
export const useVerifyTwoFactorSetup = () => {
  return useMutation({
    mutationFn: async (code: string): Promise<TwoFactorVerifySetupResponse> => {
      return AuthApiService.verifyTwoFactorSetup(code);
    },
  });
};

/**
 * Disable 2FA for current user
 */
export const useDisableTwoFactor = () => {
  return useMutation({
    mutationFn: async (password: string): Promise<{ success: boolean; message: string }> => {
      return AuthApiService.disableTwoFactor(password);
    },
  });
};

/**
 * Confirm email change using token from the email link
 */
export const useConfirmEmailChange = () => {
  return useMutation({
    mutationFn: async (token: string): Promise<{ message: string }> => {
      return AuthApiService.confirmEmailChange(token);
    },
  });
};
